/**
 * Stable backend codes the API sends as `HttpsError.details.domainCode`.
 * They are the contract for translating a failure, not the English message.
 */
export const API_DOMAIN_CODES = {
  NATIONAL_ID_ALREADY_REGISTERED: 'user_national_id_already_registered',
  ONBOARDING_ALREADY_COMPLETED: 'user_onboarding_already_completed',
  INVITE_REQUIRES_SIGN_IN: 'user_invite_requires_sign_in',
  IN_FLIGHT_LIMIT: 'api/in-flight-limit',
} as const;

export type ApiDomainCode = (typeof API_DOMAIN_CODES)[keyof typeof API_DOMAIN_CODES];

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

/**
 * Reads the callable `details` payload from the shapes Firebase and this app
 * actually produce: `details`, `customData`, or nested `customData.details`.
 */
export const getCallableErrorDetails = (
  error: unknown,
): Record<string, unknown> | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const record = error as { details?: unknown; customData?: unknown };
  const fromDetails = asRecord(record.details);
  if (fromDetails) {
    return fromDetails;
  }

  const customData = asRecord(record.customData);
  if (!customData) {
    return undefined;
  }
  if ('reason' in customData || 'domainCode' in customData) {
    return customData;
  }
  return asRecord(customData.details);
};

const readDomainCode = (details: Record<string, unknown> | undefined): string | undefined => {
  const domainCode = details?.domainCode;
  return typeof domainCode === 'string' ? domainCode : undefined;
};

/**
 * Reads the backend domain code from a callable failure. Firebase exposes the
 * server `details` payload as `details` on web and `customData` on some SDK
 * versions, and repositories may re-throw an error carrying it directly.
 */
export const getCallableDomainCode = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  if ('domainCode' in error) {
    const { domainCode } = error as { domainCode: unknown };
    if (typeof domainCode === 'string') {
      return domainCode;
    }
  }

  return readDomainCode(getCallableErrorDetails(error));
};

/** Firebase error code (`functions/already-exists`, `auth/...`), when the error carries one. */
export const getCallableErrorCode = (error: unknown): string | undefined => {
  const code = typeof error === 'object' && error !== null
    ? (error as { code?: unknown }).code
    : undefined;
  return typeof code === 'string' ? code : undefined;
};

/** True only for the callable capacity limit that can be retried safely. */
export const isInFlightLimitError = (error: unknown): boolean =>
  getCallableErrorCode(error) === 'functions/resource-exhausted'
    && getCallableDomainCode(error) === API_DOMAIN_CODES.IN_FLIGHT_LIMIT;
